/**
 * Repainting a loaded aircraft at runtime — the player's paint shop
 * (docs/formats/shading-and-paint.md §10).
 *
 * The bake ships one entry per *known* scheme: the factory colours and each
 * named ace's. The player's own aircraft cannot work that way — his two
 * choices make 64 combinations per airframe — so his machine is drawn from the
 * factory entry with the paint step redone in the browser, exactly as
 * `RB.EXE` seg `133f:0123` redoes it at load time for every object it paints.
 *
 * What that costs is one field: `ModelFaceColor.paintGroup`, the `.CLG` group
 * that claimed the face. It has to be baked because a face's colour cannot be
 * inverted back to a group — most fighters paint fabric, wings and fuselage
 * the same palette index, which is precisely the case the paint shop has to
 * pull apart.
 *
 * **Re-shading survives the repaint.** A face's `anchorIndex` is the *input*
 * to `ramp-material.ts`'s per-frame ramp walk, and every ramp has its own
 * width: recolouring 76 (grey, `SHADES.DAT` half-width 4) to 30 (red,
 * half-width 6) without also widening the half-width would walk the red ramp
 * with the grey one's stride and leave the top two shades unreachable. So a
 * group whose colour actually moves takes its new half-width from
 * `SHIPPED_PAINT_SHOP_COLOURS`, and a group left at the factory's keeps the
 * baked pair untouched.
 */

import {
  SHADE_LEVEL_COUNT,
  SHADE_ROW_COUNT,
  SHIPPED_PAINT_SHOP_COLOURS,
  SHIPPED_SHADE_DELTAS,
  shadeLevelFromCosine,
} from "@red-opus/formats";
import type { BakedModelFaceColor, BakedModelLodMesh } from "./model-geometry.ts";
import { PALETTE_ENTRY_COUNT, type BakedPaletteBody } from "./ramp-shade.ts";

/** A baked face plus the field only a repaint needs. */
export interface PaintableFaceColor extends BakedModelFaceColor {
  /** `.CLG` group 0-5, absent on a face the load-time repaint does not touch. */
  readonly paintGroup?: number;
  readonly outlineRgba?: readonly [number, number, number, number];
}

/** A LOD whose faces may carry paint groups. */
export interface PaintableLod extends BakedModelLodMesh {
  readonly faceColors: readonly PaintableFaceColor[];
}

/** `SHADES.DAT`'s half-width for one of the eight paint-shop colours, or `undefined` for any other index. */
export function paintShopHalfWidth(paletteIndex: number): number | undefined {
  return SHIPPED_PAINT_SHOP_COLOURS.find((colour) => colour.index === paletteIndex)?.halfWidth;
}

function paletteRgba(palette: BakedPaletteBody, index: number): [number, number, number, number] {
  const clamped = Math.max(0, Math.min(PALETTE_ENTRY_COUNT - 1, index));
  const [r, g, b] = palette.colors[clamped] ?? [0, 0, 0];
  return [r, g, b, 255];
}

/**
 * The bake's reference-attitude colour for one face, redone for a new anchor:
 * the model straight and level with the sun at model `+Y`, so the Lambert
 * cosine of a face is its normal's Y component (`model-paint.ts`'s
 * `REFERENCE_SUN`). Reproducing it here keeps the flat vertex colour — the one
 * the GPU falls back to when a face is unshaded, and the only one the
 * palette-less fallback material ever shows — consistent with the bake's.
 *
 * `modelUpwards` is that cosine — a face normal's **model-space Y**. Callers
 * hold their geometry in different frames (a baked LOD is still in the model's,
 * an enhanced `.glb` has already been turned into the world's), so projecting
 * the normal is the caller's job and this takes the number rather than a vector.
 */
export function referenceShadedIndex(anchor: number, halfWidth: number, shadeable: boolean, modelUpwards: number): number {
  if (!shadeable || halfWidth < 1 || halfWidth > SHADE_ROW_COUNT) return anchor;
  const level = shadeLevelFromCosine(modelUpwards);
  return anchor + (SHIPPED_SHADE_DELTAS[(halfWidth - 1) * SHADE_LEVEL_COUNT + level] ?? 0);
}

/**
 * A copy of `lod` with every painted face moved to `colours`, or `lod` itself
 * when nothing would change.
 *
 * `colours` is the six `.CLG` group colours the aircraft is to be drawn in —
 * `personalSchemeColours(factory, paint)` for a player's machine. Faces with
 * no `paintGroup` (the national markings, the cockpit interior, everything the
 * `.CLG` never lists) are left exactly as baked, which is what keeps the
 * insignia black whatever the pilot chooses.
 */
export function repaintLod(
  lod: PaintableLod,
  colours: readonly number[],
  palette: BakedPaletteBody,
): PaintableLod {
  let changed = false;
  const faceColors = lod.faceColors.map((face, triangle) => {
    const group = face.paintGroup;
    if (group === undefined) return face;
    const anchor = colours[group];
    if (anchor === undefined || anchor === face.anchorIndex) return face;
    changed = true;
    const halfWidth = paintShopHalfWidth(anchor) ?? face.halfWidth ?? 0;
    const shadeable = face.shadeable === true;
    const normal = lod.faceNormals[triangle] ?? null;
    // The engine writes the scheme colour into *both* colour bytes, so a
    // painted face never keeps a separate outline polyline (§4.2) — hence
    // dropping `outlineRgba` rather than carrying the old one through.
    const { outlineRgba: _dropped, ...kept } = face;
    return {
      ...kept,
      anchorIndex: anchor,
      halfWidth,
      colorRgba: paletteRgba(palette, referenceShadedIndex(anchor, halfWidth, shadeable, normal ? normal[1] : 0)),
    };
  });
  return changed ? { ...lod, faceColors } : lod;
}

/** True when `lod` carries the per-face paint groups a repaint needs — false for a `data/baked` from before §10. */
export function isRepaintable(lod: PaintableLod): boolean {
  return lod.faceColors.some((face) => face.paintGroup !== undefined);
}
